/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import {
  ScrollView,
  TouchableHighlight,
  TouchableOpacity,
  View,
} from 'react-native';
import { StyleSheet } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { defaultIconPack, IconPack } from '../lib/icons';
import { defaultConfig } from '../lib/default-ui-config';
import { DyteI18n, useLanguage } from '../lib/lang';
import { UIConfig } from '../types/ui-config';

import { States } from '../types/props';
import DyteText from './DyteText';
import DyteParticipantWaiting from './DyteParticipantWaiting';
import DyteParticipant from './DyteParticipant';
import { Peer } from '../types/dyte-client';
import UserAvatar from 'react-native-user-avatar';
import { SvgXml } from 'react-native-svg';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  config = defaultConfig,
  iconPack = defaultIconPack,
  states,
  size = 'sm',
  t,
}: {
  meeting: DyteClient;
  config?: UIConfig;
  iconPack?: IconPack;
  states?: States;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  t?: DyteI18n;
}) {
  const [liveStreamStageRequested, setLiveStreamStageRequested] = useState([]);
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const joined = useDyteSelector((m) => m.participants.joined);
  const waiting = useDyteSelector((m) => m.participants.waitlisted);
  const onStage = useDyteSelector((m) =>
    m.participants.joined.toArray().filter((p) => p.stageStatus === 'ON_STAGE')
  );
  const offStage = useDyteSelector((m) =>
    m.participants.joined.toArray().filter((p) => p.stageStatus !== 'ON_STAGE')
  );
  const isWebinar = useDyteSelector((m) => m.meta.viewType === 'WEBINAR');
  const isGroupCall = useDyteSelector((m) => m.meta.viewType === 'GROUP_CALL');
  const isLiveStream = useDyteSelector((m) => m.meta.viewType === 'LIVESTREAM');
  const stageRequested = joined
    .toArray()
    .filter((p) => p.stageStatus === 'REQUESTED_TO_JOIN_STAGE');
  const self = meeting.self;
  const { permissions } = meeting.self;

  const multipleRequests =
    stageRequested.length > 1 ||
    liveStreamStageRequested.length > 1 ||
    (meeting.meta.viewType === 'LIVESTREAM' &&
      liveStreamStageRequested.length > 1 &&
      permissions.acceptPresentRequests) ||
    (meeting.meta.viewType === 'WEBINAR' &&
      stageRequested.length > 1 &&
      permissions.acceptPresentRequests) ||
    (waiting.toArray().length > 1 && permissions.acceptWaitingRequests);

  const AcceptOrRejectAll = ({ userColors }: { userColors: any }) => {
    const handleRejectAll = () => {
      // Add your logic for Reject All button click here
      if (meeting.meta.viewType === 'GROUP_CALL') {
        waiting
          .toArray()
          .map((p) => meeting.participants.rejectWaitingRoomRequest(p.id));
      } else if (meeting.meta.viewType === 'WEBINAR') {
        const reqUIDs = stageRequested.map((reqP) => reqP.userId);
        meeting.stage.denyAccess(reqUIDs);
      } else if (meeting.meta.viewType === 'LIVESTREAM') {
        const reqUIDs = liveStreamStageRequested.map((req) => req.userId);
        meeting.stage.denyAccess(reqUIDs);
      }
    };

    const handleAcceptAll = () => {
      // Add your logic for Accept All button click here
      if (waiting.toArray().length > 0) {
        waiting
          .toArray()
          .map((p) => meeting.participants.acceptWaitingRoomRequest(p.id));
      }
      if (meeting.meta.viewType === 'GROUP_CALL') {
        waiting
          .toArray()
          .map((p) => meeting.participants.acceptWaitingRoomRequest(p.id));
      } else if (meeting.meta.viewType === 'WEBINAR') {
        const reqUIDs = stageRequested.map((reqP) => reqP.userId);
        meeting.stage.grantAccess(reqUIDs);
      } else if (meeting.meta.viewType === 'LIVESTREAM') {
        const reqUIDs = liveStreamStageRequested.map((req) => req.userId);
        meeting.stage.grantAccess(reqUIDs);
      }
    };

    const componentStyles = StyleSheet.create({
      container: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        paddingHorizontal: 8,
        marginVertical: 8,
      },
      button: {
        flex: 1,
        borderRadius: 8,
        padding: 8,
        alignItems: 'center',
      },
      rejectButton: {
        backgroundColor: userColors.danger,
        marginRight: 10,
      },
      acceptButton: {
        backgroundColor: userColors.success,
        marginLeft: 10,
      },
      buttonText: {
        color: userColors.text,
        fontSize: 16,
      },
    });

    return (
      <View style={componentStyles.container}>
        <TouchableHighlight
          style={[componentStyles.button, componentStyles.rejectButton]}
          underlayColor="rgba(255, 0, 0, 0.7)"
          onPress={handleRejectAll}
        >
          <DyteText style={componentStyles.buttonText}>
            {t('stage_request.deny_all')}
          </DyteText>
        </TouchableHighlight>
        <TouchableHighlight
          style={[componentStyles.button, componentStyles.acceptButton]}
          underlayColor="rgba(0, 255, 0, 0.7)"
          onPress={handleAcceptAll}
        >
          <DyteText style={componentStyles.buttonText}>
            {t('stage_request.accept_all')}
          </DyteText>
        </TouchableHighlight>
      </View>
    );
  };

  useEffect(() => {
    const fetchReq = async () => {
      if (
        meeting.self.permissions.acceptPresentRequests &&
        meeting.meta.viewType === 'LIVESTREAM'
      ) {
        const req = await meeting.stage.getAccessRequests();
        setLiveStreamStageRequested(req.stageRequests);
      }
    };
    fetchReq();
    meeting.stage.on('stageAccessRequestUpdate', fetchReq);
    return () => {
      meeting.stage.off('stageAccessRequestUpdate', fetchReq);
    };
  }, [meeting]);
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const handleTouch = () => {
    // setEnableHostOptions(false);
    setStates({
      ...storeStates,
      activeHostControlID: 'none',
    });
  };
  const styles = StyleSheet.create({
    container: {
      backgroundColor: colors.background[900],
      flexDirection: 'column',
      flex: 1,
      // width: '100%',
      paddingVertical: 20,
      zIndex: 1,
    },
    text: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    listEndMargin: {
      marginBottom: 200,
    },
    participantCard: {
      paddingHorizontal: 14,
      paddingTop: 4,
      paddingBottom: 12,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    participantCardLeft: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    hostControlContainer: {
      backgroundColor: 'rgb(44,44,44)',
      marginLeft: -5,
      marginRight: 10,
      alignItems: 'center',
      borderRadius: 5,
      position: 'absolute',
      right: -20,
      top: 30,
      width: 150,
      height: 170,
    },
    controls: {
      padding: 10,
      justifyContent: 'space-around',
      alignItems: 'flex-start',
    },
    participantNameText: {
      marginLeft: 16,
    },
    participantCardRight: {
      flexDirection: 'row',
    },
    iconStyle: {
      height: 45,
      width: 45,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background[800],
      borderRadius: 8,
      margin: 4,
    },
    requestControlContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      marginHorizontal: 16,
      marginVertical: 8,
      minHeight: 40,
    },
    acceptAllButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.success,
      borderRadius: 8,
      minHeight: 40,
      marginHorizontal: 4,
    },
    rejectAllButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      marginHorizontal: 4,
      backgroundColor: colors.danger,
      borderRadius: 8,
      minHeight: 40,
    },
  });
  return (
    <TouchableOpacity
      activeOpacity={1}
      onPress={handleTouch}
      // eslint-disable-next-line react-native/no-inline-styles
      style={{ flex: 1 }}
    >
      <ScrollView
        style={styles.container}
        // eslint-disable-next-line react-native/no-inline-styles
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {meeting.meta.viewType === 'LIVESTREAM' &&
          liveStreamStageRequested.length > 0 &&
          permissions.acceptPresentRequests && (
            <View>
              <View style={styles.text}>
                <DyteText>
                  {t('stage_request.header_title')} (
                  {liveStreamStageRequested.length})
                </DyteText>
                {/* Accept or Reject All UI */}
                {multipleRequests && <AcceptOrRejectAll userColors={colors} />}
                {liveStreamStageRequested.map((p, index) => (
                  <View style={styles.participantCard} key={index}>
                    <View style={styles.participantCardLeft}>
                      <UserAvatar
                        size={40}
                        name={p.displayName}
                        bgColor={colors.brand[300]}
                      />
                      <DyteText style={styles.participantNameText} size="lg">
                        {p.displayName}
                      </DyteText>
                    </View>
                    <View style={styles.participantCardRight}>
                      <TouchableHighlight
                        underlayColor={colors.background[800]}
                        onPress={() => meeting.stage.denyAccess([p.userId])}
                        style={styles.iconStyle}
                      >
                        <SvgXml
                          xml={iconPack.dismiss.replace(
                            'currentColor',
                            colors.danger
                          )}
                          width={25}
                          height={25}
                        />
                      </TouchableHighlight>
                      <TouchableHighlight
                        underlayColor={colors.background[800]}
                        onPress={() => meeting.stage.grantAccess([p.userId])}
                        style={styles.iconStyle}
                      >
                        <SvgXml
                          xml={iconPack.checkmark.replace(
                            'currentColor',
                            colors.success
                          )}
                          width={25}
                          height={25}
                        />
                      </TouchableHighlight>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          )}
        {meeting.meta.viewType === 'WEBINAR' &&
          stageRequested.length > 0 &&
          permissions.acceptPresentRequests && (
            <View>
              <View style={styles.text}>
                <DyteText>
                  {t('stage_request.header_title')} ({stageRequested.length})
                </DyteText>
                {/* Accept or Reject All UI */}
                {multipleRequests && <AcceptOrRejectAll userColors={colors} />}
                {stageRequested.map((p, index) => (
                  <DyteParticipantWaiting
                    meeting={meeting}
                    participant={p}
                    key={index}
                  />
                ))}
              </View>
            </View>
          )}
        {waiting.toArray().length > 0 && permissions.acceptWaitingRequests && (
          <View>
            <View style={styles.text}>
              <DyteText>
                {t('waitlist.header_title')} ({waiting.toArray().length})
              </DyteText>
              {/* Accept or Reject All UI */}
              {multipleRequests && <AcceptOrRejectAll userColors={colors} />}
              {waiting.toArray().map((p, index) => (
                <DyteParticipantWaiting
                  meeting={meeting}
                  participant={p as Peer}
                  key={index}
                />
              ))}
            </View>
          </View>
        )}
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <View style={{ zIndex: 1 }}>
          {/* In Call (Webinar/Livestream) */}
          {isWebinar &&
            (onStage.length > 0 || meeting.stage.status === 'ON_STAGE') && (
              <View style={styles.text}>
                <DyteText>
                  In Call (
                  {meeting.stage.status === 'ON_STAGE'
                    ? onStage.length + 1
                    : onStage.length}
                  )
                </DyteText>
              </View>
            )}
          {isWebinar && meeting.stage.status === 'ON_STAGE' && (
            <DyteParticipant meeting={meeting} participant={self} />
          )}
          {isWebinar &&
            onStage.length > 0 &&
            onStage.map((p, index) => (
              <DyteParticipant key={index} meeting={meeting} participant={p} />
            ))}
          {/* In Call (Group Call) */}
          {(isGroupCall || isLiveStream) && joined.toArray().length >= 0 && (
            <View style={styles.text}>
              <DyteText>In Call ({joined.toArray().length + 1})</DyteText>
            </View>
          )}
          {(isGroupCall ||
            (isLiveStream && meeting.stage.status === 'ON_STAGE')) && (
            <DyteParticipant meeting={meeting} participant={self} />
          )}
          {(isGroupCall || isLiveStream) &&
            joined.toArray().length > 0 &&
            joined
              .toArray()
              .map((p, index) => (
                <DyteParticipant
                  key={index}
                  meeting={meeting}
                  participant={p}
                />
              ))}
          {/* Viewers (Webinar) */}
          {isWebinar &&
            (offStage.length > 0 || meeting.stage.status !== 'ON_STAGE') && (
              <View style={styles.text}>
                <DyteText>
                  {t('viewers')} (
                  {meeting.stage.status !== 'ON_STAGE'
                    ? offStage.length + 1
                    : offStage.length}
                  )
                </DyteText>
              </View>
            )}
          {isWebinar &&
            offStage.length > 0 &&
            offStage.map((p, index) => (
              <DyteParticipant key={index} meeting={meeting} participant={p} />
            ))}
          {isWebinar && meeting.stage.status !== 'ON_STAGE' && (
            <DyteParticipant meeting={meeting} participant={self} />
          )}
          {<View style={styles.listEndMargin} />}
        </View>
      </ScrollView>
    </TouchableOpacity>
  );
}
